import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useArrayRef } from '../../../../utilities/common/hooks/useRefArray';
import { setRefAtIndex } from '../../../../utilities/common/utils';
import useTracking from '../../../../utilities/hooks/useTracking';
import {
  EventActions,
  EventNames,
} from '../../../../shared/constants/analytics';

export interface Tab {
  label: string;
  content: ReactNode;
  key: string;
}

interface TabViewProps {
  trackingPageTitle?: string;
  tabs: Tab[];
  defaultActiveTab?: number;
}

function TabView({
  trackingPageTitle,
  tabs,
  defaultActiveTab = 0,
}: TabViewProps) {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  const tabRefs = useArrayRef<HTMLButtonElement>();
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });

  const { trackEvent } = useTracking();
  const currentPath = useLocation().pathname;
  const pageName = currentPath.replace(/^\/d\//, '').replace(/-/g, '_');

  useEffect(() => {
    const activeRef = tabRefs.current[activeTab];
    if (!activeRef) return;

    const { offsetLeft: left, offsetWidth: width } = activeRef;
    setUnderlineStyle({ left, width });
  }, [activeTab, tabs.length]);

  return (
    <div className="flex flex-col">
      <div className="scrollbar-hidden relative w-full overflow-x-auto pt-2">
        <div className="flex w-fit gap-x-6">
          {tabs.map((tab, index) => (
            <button
              key={tab.key}
              ref={setRefAtIndex(tabRefs, index)}
              onClick={() => {
                setActiveTab(index);
                activeTab !== index &&
                  trackEvent(EventNames.WellBeingDashboard, {
                    eventLabel: trackingPageTitle || '',
                    eventAction: EventActions.click,
                    eventCategory: 'dimensions_details',
                    eventSource: pageName,
                    eventTab: tab.label,
                  });
              }}
              className={`font-figtree text-md relative cursor-pointer px-1 pb-4 font-medium whitespace-nowrap transition-colors ${
                activeTab === index
                  ? 'text-blue-700'
                  : 'text-grey-700 hover:text-blue-700'
              }`}
            >
              {tab.label}
            </button>
          ))}

          <div
            className="absolute bottom-0 h-0.5 bg-blue-700 transition-all duration-300 ease-in-out"
            style={{ left: underlineStyle.left, width: underlineStyle.width }}
          />
        </div>
      </div>
      {/* TABS CONTENT */}
      <div className="mt-6">
        {tabs.map((tab, index) => (
          <div
            key={tab.key}
            style={{ display: activeTab === index ? 'block' : 'none' }}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TabView;
